import { logger } from '@/lib/logger';
import type { ExperienceBlockDraft, ExperienceVariable } from '@matter_extractor/shared';
import { ClipboardCopy } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { READER_TEST_IDS, type ReaderMode } from '../contracts';
import { readerPlainText } from '../reader-text';
import { VariableText } from './variable-text';

export interface CopyableBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'copyable' }>;
  readonly mode: ReaderMode;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5). */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

/** Ce que la copie a répondu. `idle` tant que personne n'a cliqué. */
type CopyOutcome = 'idle' | 'copied' | 'refused';

const REFUSAL = 'La copie a échoué, sélectionne le texte et fais Ctrl+C';

/**
 * Le bloc **Texte copiable** (§9.2, issue #137) : un cadre, son texte, et
 * l'icône de copie en haut à droite.
 *
 * ## Du texte BRUT, et c'est une promesse plutôt qu'une limite
 *
 * A Texte block holds a tree with marks; this one holds a string. What one
 * copies is made to be pasted ELSEWHERE - a form, a terminal, a message - and
 * the bold does not survive the trip. Offering marks here would be promising
 * what the gesture destroys: the person would style a code, copy it, paste it,
 * and get plain text back with no way to know where the styling went.
 *
 * ## Les variables, en revanche, sont tout l'intérêt
 *
 * « Ton code est {{champ:code}} » means nothing unresolved, so the text goes
 * through the same resolve-or-name arbitration as a Checkbox label
 * (`VariableText`). And what the CLIPBOARD receives is the resolved text and
 * never the stored spelling: a person who pasted `{{champ:code}}` into a form
 * would have copied the address of a value instead of the value.
 *
 * The block PRODUCES nothing (no `variableName`) and advances no page: copying
 * is a gesture the interview learns nothing from.
 *
 * ## Copier seulement - et donc un refus explicite, qui n'est pas facultatif
 *
 * The control copies (`navigator.clipboard.writeText`) and does not fall back
 * to selecting the text for the person, because a selection she did not ask for
 * moves her caret and looks like a bug. That choice has a price, and this is
 * it: `writeText` REJECTS - a permission denied, an insecure origin, a browser
 * that has no clipboard - and a « Copié » shown on a rejection is the worst
 * outcome available. The person believes she holds a code, pastes nothing, and
 * has no idea anything failed. So the pastille reports what actually happened,
 * and the refusal tells her the one thing she can still do.
 *
 * ## Il ne copie qu'en session
 *
 * `interactive` and nowhere else: in `edit` the selection target of the frame
 * sits over the control, and a Timeline scene shows a page rather than plays
 * it. A preview that wrote to the operator's clipboard would be a page stealing
 * what she had copied to paste into the field next to it.
 */
export function CopyableBlock({
  block,
  mode,
  resolveVariable,
  variableNames,
}: CopyableBlockProps): ReactNode {
  const [outcome, setOutcome] = useState<CopyOutcome>('idle');

  const copy = (): void => {
    /* Le presse-papiers reçoit le texte RÉSOLU, jamais l'orthographe stockée. */
    const payload = readerPlainText(block.text, resolveVariable);
    const clipboard = navigator.clipboard;
    if (clipboard === undefined) {
      setOutcome('refused');
      return;
    }
    /* Le `try` n'est pas une ceinture de plus : `writeText` REJETTE sur une
     * permission refusée et JETTE sur une origine non sécurisée, et un refus qui
     * ne couvrirait qu'une des deux formes laisserait la pastille à « Copié »
     * sur l'autre - ce qui est exactement ce que ce bloc ne doit jamais dire. */
    try {
      clipboard.writeText(payload).then(
        () => setOutcome('copied'),
        (cause: unknown) => {
          logger.warn(`reader: copying block ${block.id} was refused`, cause);
          setOutcome('refused');
        },
      );
    } catch (cause) {
      logger.warn(`reader: copying block ${block.id} could not start`, cause);
      setOutcome('refused');
    }
  };

  return (
    <div className="lecteur-copiable">
      <div className="lecteur-copiable-texte">
        <VariableText
          text={block.text}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      </div>
      <button
        type="button"
        className="lecteur-copiable-bouton"
        data-testid={READER_TEST_IDS.copy}
        aria-label="Copier le texte"
        onClick={mode === 'interactive' ? copy : undefined}
      >
        <ClipboardCopy size={16} aria-hidden="true" />
      </button>
      {/*
        `<output>` et pas un `<p role="status">` : c'est l'élément dont le rôle
        implicite EST `status`, donc une zone polie qu'un lecteur d'écran
        annonce sans interrompre. Biome le demande, et il a raison ici : ce que
        cette ligne porte est le RÉSULTAT d'un geste, ce qui est exactement ce
        que l'élément nomme.
      */}
      {outcome === 'idle' ? null : (
        <output
          className="lecteur-copiable-retour"
          data-testid={READER_TEST_IDS.copyOutcome}
          data-outcome={outcome}
        >
          {outcome === 'copied' ? 'Copié' : REFUSAL}
        </output>
      )}
    </div>
  );
}
